/**
 * Filter stage component for selecting countries before tournament.
 *
 * Displayed when a tournament has >32 countries.
 * Users tap flags to select countries they "like".
 * Countries are grouped by geographic region with sticky headers.
 * Selected countries proceed to groups (if >16) or bracket (if ≤16).
 */

import { motion } from 'framer-motion';
import {
  Check,
  CheckCircle2,
  ChevronRight,
  Circle,
  Info,
  Search,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { CountryInfoModal } from '@/components/CountryInfoModal';
import { Button } from '@/components/ui/button';
import { getCountryByCode } from '@/data/countries';
import { geographicRegions } from '@/data/countries/regions';
import type { Country, CountryCode } from '@/data/countries/types';
import { cn } from '@/lib/utils';
import { useTournament } from './TournamentProvider';

const MIN_SELECTION = 8;

interface FilterCellProps {
  country: Country;
  isSelected: boolean;
  onToggle: () => void;
  onInfo: () => void;
}

function FilterCell({
  country,
  isSelected,
  onToggle,
  onInfo,
}: FilterCellProps) {
  return (
    <motion.button
      onClick={onToggle}
      whileTap={{ scale: 0.97 }}
      className={cn(
        'relative aspect-square rounded-lg border-2 p-1.5',
        'flex flex-col items-center justify-center gap-0.5',
        'transition-colors touch-manipulation',
        isSelected
          ? 'bg-green-50 border-green-400 dark:bg-green-950 dark:border-green-600'
          : 'bg-card border-border hover:border-primary/50',
      )}
    >
      <span className="text-2xl sm:text-3xl">{country.flag}</span>
      <span className="text-[10px] sm:text-xs font-medium text-center truncate w-full px-0.5 leading-tight">
        {country.name}
      </span>

      {/* Info/Check icon - top right */}
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          onInfo();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
            e.preventDefault();
            onInfo();
          }
        }}
        className={cn(
          'absolute top-1 right-1 p-1 rounded-full transition-colors cursor-pointer',
          isSelected
            ? 'bg-green-500 text-white'
            : 'bg-muted/80 hover:bg-muted text-muted-foreground',
        )}
        aria-label={isSelected ? 'Selected' : 'View info'}
      >
        {isSelected ? (
          <Check className="w-3 h-3" />
        ) : (
          <Info className="w-3 h-3" />
        )}
      </span>
    </motion.button>
  );
}

interface RegionGroup {
  id: string;
  name: string;
  icon: string;
  countries: Country[];
}

export function FilterStage() {
  const { state, actions } = useTournament();
  const [infoCode, setInfoCode] = useState<CountryCode | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Group countries by geographic region
  const regionGroups = useMemo(() => {
    if (!state?.originalCountries) return [];

    const originalSet = new Set(state.originalCountries);
    const assignedCountries = new Set<CountryCode>();
    const groups: RegionGroup[] = [];

    // Go through geographic regions and find matching countries
    for (const region of geographicRegions) {
      const countriesInRegion: Country[] = [];

      for (const code of region.countries) {
        if (
          originalSet.has(code as CountryCode) &&
          !assignedCountries.has(code as CountryCode)
        ) {
          const country = getCountryByCode(code as CountryCode);
          if (country) {
            countriesInRegion.push(country);
            assignedCountries.add(code as CountryCode);
          }
        }
      }

      if (countriesInRegion.length > 0) {
        groups.push({
          id: region.id,
          name: region.name,
          icon: region.icon ?? '',
          countries: countriesInRegion.sort((a, b) =>
            a.name.localeCompare(b.name),
          ),
        });
      }
    }

    // Add any remaining countries not in a geographic region to "Other"
    const remainingCountries: Country[] = [];
    for (const code of state.originalCountries) {
      if (!assignedCountries.has(code)) {
        const country = getCountryByCode(code);
        if (country) {
          remainingCountries.push(country);
        }
      }
    }

    if (remainingCountries.length > 0) {
      groups.push({
        id: 'other',
        name: 'Other',
        icon: '🌍',
        countries: remainingCountries.sort((a, b) =>
          a.name.localeCompare(b.name),
        ),
      });
    }

    return groups;
  }, [state?.originalCountries]);

  // Filter groups based on search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return regionGroups;

    const query = searchQuery.toLowerCase().trim();

    return regionGroups
      .map((group) => ({
        ...group,
        countries: group.countries.filter(
          (country) =>
            country.name.toLowerCase().includes(query) ||
            country.code.toLowerCase().includes(query),
        ),
      }))
      .filter((group) => group.countries.length > 0);
  }, [regionGroups, searchQuery]);

  const selectedSet = useMemo(() => {
    return new Set(state?.filterSelection ?? []);
  }, [state?.filterSelection]);

  if (!state || state.status !== 'filter_stage') return null;

  const selectedCount = selectedSet.size;
  const canContinue = selectedCount >= MIN_SELECTION;

  // Determine what happens next
  const nextStageText = selectedCount <= 16 ? 'bracket' : 'groups';

  const handleSelectAllInGroup = (group: RegionGroup) => {
    const codes = group.countries.map((c) => c.code);
    actions.setFilterCountries(codes, true);
  };

  const handleDeselectAllInGroup = (group: RegionGroup) => {
    const codes = group.countries.map((c) => c.code);
    actions.setFilterCountries(codes, false);
  };

  const getGroupSelectionState = (group: RegionGroup) => {
    const selectedInGroup = group.countries.filter((c) =>
      selectedSet.has(c.code),
    ).length;
    if (selectedInGroup === 0) return 'none';
    if (selectedInGroup === group.countries.length) return 'all';
    return 'partial';
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="shrink-0 mb-3">
        <h2 className="font-display text-lg font-semibold">
          Select countries you like
        </h2>
        <p className="text-sm text-muted-foreground">
          {selectedCount} selected
          {selectedCount > 0 && ` → ${nextStageText}`}
          {selectedCount < MIN_SELECTION && ` (${MIN_SELECTION} minimum)`}
        </p>
      </div>

      {/* Search bar */}
      <div className="shrink-0 mb-3 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search countries..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={cn(
            'w-full pl-9 pr-9 py-2 rounded-lg border bg-background',
            'text-sm placeholder:text-muted-foreground',
            'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary',
          )}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-muted"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Scrollable grouped grid */}
      <div className="flex-1 min-h-0 overflow-y-auto -mx-4 px-4 pb-20">
        {filteredGroups.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No countries found matching "{searchQuery}"
          </div>
        ) : (
          filteredGroups.map((group) => {
            const selectionState = getGroupSelectionState(group);
            const selectedInGroup = group.countries.filter((c) =>
              selectedSet.has(c.code),
            ).length;

            return (
              <div key={group.id} className="mb-4">
                {/* Sticky header */}
                <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm py-2 -mx-4 px-4 border-b border-border/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{group.icon}</span>
                      <h3 className="font-semibold text-sm">{group.name}</h3>
                      <span className="text-xs text-muted-foreground">
                        ({selectedInGroup}/{group.countries.length})
                      </span>
                    </div>

                    {/* Select all / Deselect all button */}
                    <button
                      onClick={() => {
                        if (selectionState === 'all') {
                          handleDeselectAllInGroup(group);
                        } else {
                          handleSelectAllInGroup(group);
                        }
                      }}
                      className={cn(
                        'flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors',
                        selectionState === 'all'
                          ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900 dark:text-green-300 dark:hover:bg-green-800'
                          : 'bg-muted hover:bg-muted/80 text-muted-foreground',
                      )}
                    >
                      {selectionState === 'all' ? (
                        <>
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          All selected
                        </>
                      ) : (
                        <>
                          <Circle className="w-3.5 h-3.5" />
                          Select all
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Country grid */}
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 mt-2">
                  {group.countries.map((country) => {
                    const isSelected = selectedSet.has(country.code);

                    return (
                      <FilterCell
                        key={country.code}
                        country={country}
                        isSelected={isSelected}
                        onToggle={() =>
                          actions.toggleFilterCountry(country.code)
                        }
                        onInfo={() => setInfoCode(country.code)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Sticky button at bottom */}
      <div className="shrink-0 fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-background via-background to-transparent pointer-events-none lg:relative lg:bg-none lg:p-0 lg:pt-3">
        <div className="pointer-events-auto max-w-lg mx-auto lg:max-w-none">
          <Button
            onClick={actions.completeFilterStage}
            disabled={!canContinue}
            className="w-full shadow-lg lg:shadow-none"
            size="lg"
          >
            Continue with {selectedCount} countries
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
          {!canContinue && (
            <p className="text-xs text-muted-foreground text-center mt-2">
              Select at least {MIN_SELECTION} countries to continue
            </p>
          )}
        </div>
      </div>

      {/* Info modal */}
      {infoCode && (
        <CountryInfoModal
          code={infoCode}
          open={!!infoCode}
          onOpenChange={(open) => !open && setInfoCode(null)}
          showMap
        />
      )}
    </div>
  );
}
